import { useState } from "react";
import { ArrowRight, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/PageHeader";
import { assist, type AssistanceIntent, type AssistanceRefinement, type AssistanceResponse, type AssistanceLookup, type KeptWork } from "@/api/composer";
import { AssistanceComparisonPanel } from "./AssistanceComparisonPanel";
import { AssistanceGuidancePanel } from "./AssistanceGuidancePanel";

/** Explicit diagnostic continuation. No request runs on mount or selection. */
export function ContinueKeptWorkPanel({ item, disabled, onUseRequirements }: {
  item: KeptWork;
  disabled: boolean;
  onUseRequirements: (context: NonNullable<AssistanceResponse["workingContext"]>) => void;
}) {
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const [lookupJson, setLookupJson] = useState("{}");
  const [refinement, setRefinement] = useState("");
  const [result, setResult] = useState<AssistanceResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function preview(intentChoice?: AssistanceIntent) {
    setBusy(true); setError(""); setResult(null);
    try {
      const changes = refinement.trim() ? JSON.parse(refinement) as AssistanceRefinement : undefined;
      const filters = JSON.parse(lookupJson) as Omit<AssistanceLookup, "query">;
      setResult((await assist(text, intentChoice, query.trim() ? { ...filters, query: query.trim() } : undefined, {
        continueFrom: { id: item.id, expectedVersion: item.version }, refinement: changes,
      })).data);
    } catch (failure) { setError(failure instanceof Error ? failure.message : "Continuation failed"); }
    finally { setBusy(false); }
  }
  return <section className="space-y-2 rounded-lg border p-3" aria-label="Continue saved need">
    <h4 className="font-semibold">Continue saved need</h4>
    <p className="text-xs">This sends your follow-up, original request, requirements and unresolved questions to the configured model.
      Saved drafts and source content are excluded. It does not save, reopen or publish anything.</p>
    {disabled && <p className="text-xs">Save or reload editor changes before continuing this saved version.</p>}
    <fieldset disabled={busy || disabled} className="space-y-2 text-sm">
      <label className="block">Follow-up
        <input value={text} maxLength={10000} className="block w-full rounded border p-2"
          onChange={event => { setText(event.target.value); setResult(null); }} />
      </label>
      <label className="block">Search filters (optional JSON)
        <textarea value={lookupJson} className="block w-full rounded border p-2 font-mono text-xs" rows={2}
          onChange={event => { setLookupJson(event.target.value); setResult(null); }} />
      </label>
      <label className="block">Search phrase for new options (optional)
        <input value={query} maxLength={300} className="block w-full rounded border p-2"
          onChange={event => { setQuery(event.target.value); setResult(null); }} />
      </label>
      <label className="block">Working requirements (optional JSON)
        <textarea value={refinement} className="block w-full rounded border p-2 font-mono text-xs" rows={3}
          placeholder={'{"constraints":[{"name":"walking","value":"short distances"}],"unresolvedQuestions":[]}'}
          onChange={event => { setRefinement(event.target.value); setResult(null); }} />
      </label>
      <p className="text-xs">An included array replaces that working list. An empty array clears it. Omitted lists remain unchanged.</p>
      <Button size="sm" variant="secondary" disabled={busy || disabled || text.trim().length < 3}
        onClick={() => preview()}><ArrowRight className="mr-1 h-4 w-4" />Continue saved need</Button>
    </fieldset>
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    {result && <div className="space-y-2 text-sm" aria-live="polite">
      <p>{result.message}</p>
      <AssistanceGuidancePanel guidance={result.guidance} disabled={busy || disabled} onChoose={search => {
        setQuery(search.query);
        const options = { features: search.features, city: search.city ?? undefined, state: search.state ?? undefined };
        setLookupJson(JSON.stringify(options));
      }} />
      <p>{result.memberIntent ?? "Unresolved intent"} · {result.handoff?.feature ?? "No handoff"} · Search: {result.capabilities.retrieval}</p>
      {result.choices.map(choice => <Button key={choice.intent} size="sm" variant="secondary"
        disabled={busy || disabled} onClick={() => preview(choice.intent)}>{choice.label}</Button>)}
      {result.resources.map(resource => <p key={resource.name}>{resource.name}: {resource.contact}</p>)}
      {result.workingContext && <>
        <p className="font-semibold">Member requirements, not verified facts</p>
        <ul>{result.workingContext.constraints.map((constraint, index) => <li key={index}>{constraint.name}: {constraint.value}</li>)}</ul>
        <ul>{result.workingContext.unresolvedQuestions.map((question, index) => <li key={index}>{question}</li>)}</ul>
        <p>Saved source check: {result.workingContext.selectedSources.status}.
          Unavailable references: {result.workingContext.selectedSources.unavailableReferences.length}.</p>
        {result.workingContext.selectedSources.sources.map(source => <p key={`${source.reference.type}:${source.reference.id}`}>
          Previously selected: {source.title} · {source.destinationPath}</p>)}
        <Button size="sm" variant="secondary" disabled={busy || disabled}
          onClick={() => onUseRequirements(result.workingContext!)}><ListChecks className="mr-1 h-4 w-4" />Use requirements in editor</Button>
        <p className="text-xs">This changes the editor only. Use Save changes to keep it.</p>
      </>}
      {result.retrieval.sources.map(source => <article key={`${source.reference.type}:${source.reference.id}`} className="rounded border p-2">
        <h5 className="font-semibold">{source.title}</h5><p>{source.excerpt}</p><p className="text-xs">{source.destinationPath}</p>
      </article>)}
      {result.retrieval.notes.map(note => <p key={note} className="text-xs">{note}</p>)}
      {result.responseType === "feature_handoff" && (result.memberIntent === "find" || result.memberIntent === "plan")
        && <AssistanceComparisonPanel text={text} intent={result.memberIntent} disabled={busy || disabled}
          sources={[...(result.workingContext?.selectedSources.sources ?? []), ...result.retrieval.sources]}
          continuation={{ continueFrom: { id: item.id, expectedVersion: item.version },
            refinement: result.workingContext ? { constraints: result.workingContext.constraints,
              unresolvedQuestions: result.workingContext.unresolvedQuestions } : undefined }} />}
    </div>}
  </section>;
}
